<script>
  import { navigate } from '../../lib/stores/router.svelte.js';
  import {
    authState,
    isUserAnonymous,
    initiateDiscordOAuth,
  } from '../../lib/stores/auth.svelte.js';
  import { getInviteInfo, joinGroupWithToken } from '../../lib/services/groups-api.js';
  import { isInviteExpired } from '../../lib/logic/groups-logic.js';
  import { refreshUserGroups } from '../../lib/stores/groups.svelte.js';

  let { groupId, token } = $props();

  let loading = $state(true);
  let joining = $state(false);
  let inviteInfo = $state(null);
  let error = $state('');

  $effect(() => {
    if (authState.ready) {
      loadInviteInfo();
    }
  });

  async function loadInviteInfo() {
    loading = true;
    error = '';
    try {
      const info = await getInviteInfo(groupId, token);
      if (!info) {
        error = 'This invite link is invalid or has been deleted.';
        return;
      }
      if (!info.invite.active) {
        error = 'This invite link has been revoked.';
        return;
      }
      if (isInviteExpired(info.invite.expiresAt)) {
        error = 'This invite link has expired.';
        return;
      }
      inviteInfo = info;
    } catch (err) {
      error = 'Failed to load invite info. Please check the link and try again.';
      console.error('[GroupJoinPage] Error loading invite:', err);
    } finally {
      loading = false;
    }
  }

  async function handleJoin() {
    joining = true;
    error = '';
    try {
      const result = await joinGroupWithToken(groupId, token);
      if (result.success) {
        await refreshUserGroups();
        navigate('group-detail', { groupId });
      }
    } catch (err) {
      error = err.message || 'Failed to join group. Please try again.';
    } finally {
      joining = false;
    }
  }

  function handleSignIn() {
    initiateDiscordOAuth(window.location.pathname);
  }
</script>

<main class="join-page">
  <div class="join-container">
    {#if loading}
      <div class="join-loading">Loading invite...</div>
    {:else if error}
      <div class="join-card error-card">
        <div class="join-icon">❌</div>
        <h1 class="join-title">Invite Invalid</h1>
        <p class="join-subtitle">{error}</p>
        <button class="btn-secondary" onclick={() => navigate('home')}>Go Home</button>
      </div>
    {:else if isUserAnonymous()}
      <div class="join-card">
        <div class="join-icon">👥</div>
        <h1 class="join-title">Join {inviteInfo?.group?.name || 'Group'}</h1>
        <p class="join-subtitle">Sign in with Discord to join this group.</p>
        <button class="btn-discord" onclick={handleSignIn}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path
              d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"
            />
          </svg>
          Sign in with Discord
        </button>
      </div>
    {:else if inviteInfo}
      <div class="join-card">
        <div class="join-icon">👥</div>
        <h1 class="join-title">Join {inviteInfo.group?.name || 'Group'}</h1>
        {#if inviteInfo.group?.description}
          <p class="join-subtitle">{inviteInfo.group.description}</p>
        {:else}
          <p class="join-subtitle">You've been invited to join this group.</p>
        {/if}

        {#if error}
          <div class="join-error">{error}</div>
        {/if}

        <div class="join-actions">
          <button class="btn-secondary" onclick={() => navigate('home')} disabled={joining}>
            Decline
          </button>
          <button class="btn-primary" onclick={handleJoin} disabled={joining}>
            {joining ? 'Joining...' : `Join ${inviteInfo.group?.name || 'Group'}`}
          </button>
        </div>
      </div>
    {/if}
  </div>
</main>

<style>
  .join-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
  }

  .join-container {
    width: 100%;
    max-width: 400px;
  }

  .join-loading {
    text-align: center;
    color: #6b7280;
  }

  .join-card {
    background: white;
    border-radius: 20px;
    padding: 40px 32px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .join-icon {
    font-size: 3rem;
  }

  .join-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }

  .join-subtitle {
    font-size: 15px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
  }

  .join-error {
    font-size: 13px;
    color: #ef4444;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 10px 16px;
    width: 100%;
    text-align: left;
  }

  .join-actions {
    display: flex;
    gap: 8px;
    width: 100%;
    justify-content: center;
  }

  .btn-primary {
    padding: 12px 24px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
    font-family: inherit;
  }

  .btn-primary:hover:not(:disabled) {
    background: #2563eb;
  }

  .btn-secondary {
    padding: 12px 24px;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s;
    font-family: inherit;
  }

  .btn-secondary:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .btn-primary:disabled,
  .btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-discord {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #5865f2;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
    font-family: inherit;
  }

  .btn-discord:hover {
    background: #4752c4;
  }

  .error-card {
    border: 1px solid #fecaca;
  }
</style>
